import React, { ChangeEvent, useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import {
  getCustomerFirstName,
  getDidUploadFail,
  getDocumentsToBeUploaded,
  getDocumentsToBeUploadedCategories,
  getIsUploadPageLoaded,
  getIsUploadPending,
  getSession,
  getUserUploadedFiles
} from '../../redux/slices/appData/app-data.selectors';
import {
  Button,
  ButtonContainer,
  ContentSection,
  ErrorMessage,
  Heading,
  Paragraph,
  Spinner,
  UnorderedList,
  Upload,
  UploadContainer,
  UploadItem
} from '@vwfs-bronson/bronson-react';
import { useTranslation } from 'react-i18next';
import { deleteFile, setDocumentsSubmitted, uploadFiles } from '../../redux/slices/appData/app-data.actions';
import { UploadFileBody } from '../../models/upload.model';
import { setUiUploadError, setUploadPageLoaded } from '../../redux/slices/appData/app-data.slice';
import { useNavigate } from 'react-router-dom';
import { trackingService } from '../../tracking/tracking.service';
import { SUPPORTED_EXTENSIONS, MAX_FILE_SIZE, formatFileSize } from './utils';

export const UploadPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const session = useAppSelector(getSession);

  const customerName = useAppSelector(getCustomerFirstName);
  const isUploadPending = useAppSelector(getIsUploadPending);
  const storedFiles = useAppSelector(getUserUploadedFiles);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>(storedFiles || []);
  const didUploadFail = useAppSelector(getDidUploadFail);
  const documentsToBeUploadedCategories = useAppSelector(getDocumentsToBeUploadedCategories);
  const documentsToBeUploaded = useAppSelector(getDocumentsToBeUploaded);
  const isUploadPageLoaded = useAppSelector(getIsUploadPageLoaded);
  const [uploadError, setUploadError] = React.useState<string | null>();

  const validateFile = (file: File | null) => {
    if (file?.size && file.size > MAX_FILE_SIZE) {
      setUploadError(t('upload:validation:fileTooBig', { fileSize: formatFileSize(MAX_FILE_SIZE) }));
      return false;
    }
    if (SUPPORTED_EXTENSIONS.some(fileType => file?.name.toLowerCase().endsWith(fileType))) {
      return true;
    }

    const validFormats = SUPPORTED_EXTENSIONS.map(format => format.replace('.', '').toUpperCase())
      .filter(format => format !== 'JPEG')
      .join(', ');
    setUploadError(t('upload:validation:fileMimeWrong', { validFormats }));
    return false;
  };

  const handleFileRemoval = async (fileId: string) => {
    dispatch(
      deleteFile({
        storefrontId: session?.id as string,
        token: session?.token as string,
        fileId: fileId
      })
    );
    setUploadedFiles(prevFiles => prevFiles.filter(file => file.fileId !== fileId));
  };

  const uploadDoc = async (e: ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    if (e.target.value !== '') {
      dispatch(setUiUploadError(false));
      e.preventDefault();
      const loadedFiles = e.currentTarget.files;
      if (!loadedFiles) {
        return;
      }

      const filesToUpload: UploadFileBody[] = [];
      for (const file of loadedFiles) {
        const isFileValid = validateFile(file);
        if (isFileValid) {
          const fileBody: UploadFileBody = {
            filename: file.name,
            size: file.size,
            mimeType: file.type,
            tags: ['user-upload'],
            file: file
          };

          filesToUpload.push(fileBody);
        }
      }
      if (filesToUpload.length > 0) {
        const response: string[] = await dispatch(
          uploadFiles({
            storefrontId: session?.id as string,
            token: session?.token as string,
            files: filesToUpload
          })
        ).unwrap();

        const uploadedFilesData = filesToUpload.map((file, index) => ({
          file: {
            filename: file.filename,
            size: file.size
          },
          fileId: response[index]
        }));

        setUploadedFiles(prevFiles => [...prevFiles, ...uploadedFilesData]);
      }
    }
  };

  const submitDocuments = () => {
    dispatch(
      setDocumentsSubmitted({
        storefrontId: session?.id as string,
        token: session?.token as string,
        navigate: navigate
      })
    );
  };

  useEffect(() => {
    if (!isUploadPageLoaded) {
      trackingService.trackPageView('Document upload', 'Document upload');
      dispatch(setUploadPageLoaded(true));
    }
  }, []);

  useEffect(() => {
    if (storedFiles && storedFiles.length > 0 && uploadedFiles.length === 0) {
      setUploadedFiles(storedFiles);
    }
  }, [storedFiles, uploadedFiles]);

  return (
    <>
      {isUploadPending && <Spinner fullPage center></Spinner>}
      <Heading level={4}>{t('upload:heading')}</Heading>
      <Paragraph>{t('upload:infoTextSalutation', { customerName })}</Paragraph>
      <Paragraph>{t('upload:infoText')}</Paragraph>

      <ContentSection>
        {documentsToBeUploadedCategories.map(documentCategory => (
          <>
            <Heading level={5} key={documentCategory.customCategoryCode}>
              {documentCategory.customCategoryDisplayLabel}
            </Heading>
            {documentCategory.customCategorySupplementaryText ? (
              <Paragraph>{documentCategory.customCategorySupplementaryText}</Paragraph>
            ) : (
              <></>
            )}
            <UnorderedList>
              <>
                {documentsToBeUploaded
                  .filter(document => document.customCategoryCode === documentCategory.customCategoryCode)
                  .map(document => (
                    <UnorderedList.Item key={`${document.customDocumentId}_${document.customDocumentCode}`}>
                      {document.displayLabel}
                      {document.description ? ` ${document.description}` : <></>}
                    </UnorderedList.Item>
                  ))}
              </>
            </UnorderedList>
          </>
        ))}
      </ContentSection>

      <ContentSection>
        <UploadContainer
          upload={
            <Upload
              descriptionText={t(`upload:uploadLabel`)}
              descriptionSupplementaryText={t(`upload:supplementaryUploadLabel`)}
              onChange={uploadDoc}
              accept={SUPPORTED_EXTENSIONS.join(',')}
              multiple={true}
              id="upload-container"
            />
          }
        />
        {didUploadFail && <ErrorMessage>{t('upload:validation:uploadError')}</ErrorMessage>}
        {uploadError && !isUploadPending && <ErrorMessage>{uploadError}</ErrorMessage>}
        {uploadedFiles.map(file => (
          <UploadItem
            key={file.fileId}
            className={`file-${file.fileId} u-mt`}
            icon="semantic-checkmark"
            fileName={file.file.filename}
            fileSize={formatFileSize(file.file.size)}
            hideMainButton
            onSecondaryButtonClick={() => handleFileRemoval(file.fileId)}
          />
        ))}
      </ContentSection>

      <ButtonContainer center>
        <Button disabled={uploadedFiles.length < 1} onClick={submitDocuments}>
          {t('upload:button:submit')}
        </Button>
      </ButtonContainer>
    </>
  );
};
